import { useState, useMemo } from 'react';
import {
  useListFinCounterpartiesQuery,
  useCreateOrUpdateCounterpartyMutation,
  useSync1CApiMutation,
  type FinCounterpartyRow,
  type UpsertCounterpartyInput,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconPlus, IconRefresh, IconX } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  ActionsGroup,
  SyncToast,
} from './styled';
import { CounterpartyKpiGrid } from './CounterpartyKpiGrid';
import { CounterpartyFilterBar } from './CounterpartyFilterBar';
import { CounterpartyTable } from './CounterpartyTable';
import { CounterpartyModal } from './CounterpartyModal';

export function CounterpartiesView() {
  const { can_write } = useAgentPermission('finance');
  const { data: counterparties = [], isLoading, isError, refetch } = useListFinCounterpartiesQuery();
  const [sync1C, { isLoading: isSyncing }] = useSync1CApiMutation();
  const [upsertCounterparty, { isLoading: isSaving }] = useCreateOrUpdateCounterpartyMutation();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');

  const [syncResult, setSyncResult] = useState<{ success: boolean; text: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FinCounterpartyRow | null>(null);

  const openCreateModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item: FinCounterpartyRow) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSync1C = async () => {
    try {
      setSyncResult(null);
      const res = await sync1C().unwrap();
      setSyncResult({
        success: true,
        text: `Синхронизация 1С завершена: ${res.message} (режим: ${res.connection_mode})`,
      });
      refetch();
    } catch (err: unknown) {
      setSyncResult({
        success: false,
        text: `Ошибка синхронизации с 1С: ${String(err)}`,
      });
    }
  };

  const handleSaveCounterparty = async (payload: UpsertCounterpartyInput) => {
    await upsertCounterparty(payload).unwrap();
    refetch();
  };

  // KPI расчеты
  const totalCount = counterparties.length;
  const withBankDetailsCount = useMemo(
    () =>
      counterparties.filter(
        (c) =>
          c.account_number &&
          c.account_number.length === 20 &&
          c.bik &&
          c.bik.length === 9
      ).length,
    [counterparties]
  );
  const synced1CCount = useMemo(
    () => counterparties.filter((c) => c.source.startsWith('1c')).length,
    [counterparties]
  );
  const activeSuppliersCount = useMemo(
    () =>
      counterparties.filter(
        (c) => c.counterparty_type === 'supplier' || c.counterparty_type === 'subcontractor'
      ).length,
    [counterparties]
  );

  // Фильтрация
  const filtered = useMemo(() => {
    return counterparties.filter((c) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesName = c.name.toLowerCase().includes(q) || (c.full_name?.toLowerCase().includes(q));
        const matchesInn = c.inn.includes(q);
        const matchesBik = c.bik?.includes(q);
        const matchesAcc = c.account_number?.includes(q);
        if (!matchesName && !matchesInn && !matchesBik && !matchesAcc) return false;
      }
      if (typeFilter !== 'all' && c.counterparty_type !== typeFilter) return false;
      if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
      if (verifiedFilter === 'verified' && !c.is_verified) return false;
      if (verifiedFilter === 'unverified' && c.is_verified) return false;
      return true;
    });
  }, [counterparties, search, typeFilter, sourceFilter, verifiedFilter]);

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Справочник контрагентов и интеграция с 1С</Title>
          <Subtitle>
            Единая база поставщиков, подрядчиков и банковских реквизитов. Автопополнение из 1С и выписок банка.
          </Subtitle>
        </TitleBox>
        <ActionsGroup>
          <Button
            $variant="secondary"
            onClick={handleSync1C}
            disabled={isSyncing || !can_write}
          >
            <IconRefresh size={16} />
            {isSyncing ? 'Синхронизация…' : 'Синхронизация с 1С'}
          </Button>
          <Button
            $variant="primary"
            onClick={openCreateModal}
            disabled={!can_write}
          >
            <IconPlus size={16} />
            Добавить контрагента
          </Button>
        </ActionsGroup>
      </HeaderRow>

      {syncResult && (
        <SyncToast $success={syncResult.success}>
          <span>{syncResult.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setSyncResult(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </SyncToast>
      )}

      {/* KPI карточки */}
      <CounterpartyKpiGrid
        metrics={{
          totalCount,
          withBankDetailsCount,
          synced1CCount,
          activeSuppliersCount,
        }}
      />

      {/* Фильтры */}
      <CounterpartyFilterBar
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        verifiedFilter={verifiedFilter}
        onVerifiedFilterChange={setVerifiedFilter}
      />

      {/* Таблица */}
      <CounterpartyTable
        counterparties={filtered}
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
        onEditItem={openEditModal}
        canWrite={Boolean(can_write)}
      />

      {/* Модалка редактирования / создания */}
      <CounterpartyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editingItem={editingItem}
        onSave={handleSaveCounterparty}
        isSaving={isSaving}
      />
    </Container>
  );
}
